import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { timeLookupsAPI, timeProfilesAPI, timeReportsAPI } from '../services/api';
import { TimeCustomerLookup, TimeProfile, TimeProjectLookup, TimeReportFilter, TimeWeeklyReport } from '../types';
import { endOfWeekIso, formatDate, formatDurationMinutes, startOfWeekIso } from '../utils';
import DateInput from './DateInput';
import TimeSelect, { TimeSelectOption } from './TimeSelect';

const billingFilterOptions: TimeSelectOption<string>[] = [
  { value: 'all', label: 'All billing' },
  { value: 'true', label: 'Billable' },
  { value: 'false', label: 'Non-billable' },
];

const WeeklyTimesheetPage: React.FC = () => {
  const { canViewAllReports } = useAuth();
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [weeks, setWeeks] = useState<TimeWeeklyReport[]>([]);
  const [filters, setFilters] = useState<TimeReportFilter>({
    startDate: startOfWeekIso(),
    endDate: endOfWeekIso(),
  });
  const [loading, setLoading] = useState(true);
  const filteredProjects = useMemo(() => {
    if (!filters.customerId) {
      return projects;
    }

    return projects.filter(project => project.customerId === filters.customerId);
  }, [filters.customerId, projects]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileData, projectData, customerData, weeklyData] = await Promise.all([
        timeProfilesAPI.getProfiles(false),
        timeLookupsAPI.getProjects(),
        timeLookupsAPI.getCustomers(),
        timeReportsAPI.getWeekly(filters),
      ]);
      setProfiles(profileData);
      setProjects(projectData);
      setCustomers(customerData);
      setWeeks(weeklyData);
    } catch {
      toast.error('Weekly timesheet could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, canViewAllReports]);

  useEffect(() => {
    if (!canViewAllReports && filters.timeProfileId) {
      setFilters(prev => ({ ...prev, timeProfileId: undefined }));
    }
  }, [filters.timeProfileId, canViewAllReports]);

  const totals = useMemo(() => {
    const profiles = weeks.flatMap(week => week.profiles);
    return {
      minutes: profiles.reduce((sum, profile) => sum + profile.totalMinutes, 0),
      missingMinutes: profiles.reduce((sum, profile) => sum + Math.round(profile.missingHours * 60), 0),
      warnings: profiles.filter(profile => profile.warning).length,
    };
  }, [weeks]);

  const updateFilter = (patch: Partial<TimeReportFilter>) => {
    setFilters(prev => ({ ...prev, ...patch }));
  };

  const updateCustomerFilter = (customerId: number | undefined) => {
    setFilters(prev => {
      const selectedProjectStillMatches = !customerId || !prev.projectId ||
        projects.some(project => project.id === prev.projectId && project.customerId === customerId);

      return {
        ...prev,
        customerId,
        projectId: selectedProjectStillMatches ? prev.projectId : undefined,
      };
    });
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Weekly</h1>
          <p>{formatDate(filters.startDate)} - {formatDate(filters.endDate)}</p>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <span>Total</span>
          <strong>{formatDurationMinutes(totals.minutes)}</strong>
        </div>
        <div className="stat-card">
          <span>Missing</span>
          <strong>{formatDurationMinutes(totals.missingMinutes)}</strong>
        </div>
        <div className="stat-card">
          <span>Warnings</span>
          <strong>{totals.warnings}</strong>
        </div>
      </div>

      <section className="card">
        <div className="card-header">
          <h5 className="card-title">Timesheet</h5>
        </div>
        <div className="card-body">
          <div className="filter-bar">
            <DateInput
              className="form-control"
              ariaLabel="Start date filter"
              value={filters.startDate || ''}
              onChange={(value) => updateFilter({ startDate: value })}
            />
            <DateInput
              className="form-control"
              ariaLabel="End date filter"
              value={filters.endDate || ''}
              onChange={(value) => updateFilter({ endDate: value })}
            />
            {canViewAllReports && (
              <TimeSelect
                inputId="weekly-employee-filter"
                ariaLabel="Member filter"
                options={[
                  { value: 0, label: 'All members' },
                  ...profiles.map(profile => ({ value: profile.id, label: profile.displayName })),
                ]}
                value={filters.timeProfileId || 0}
                onChange={(value) => updateFilter({ timeProfileId: Number(value || 0) || undefined })}
                placeholder="All members"
              />
            )}
            <TimeSelect
              inputId="weekly-customer-filter"
              ariaLabel="Customer filter"
              options={[
                { value: 0, label: 'All customers' },
                ...customers.map(customer => ({ value: customer.id, label: customer.title })),
              ]}
              value={filters.customerId || 0}
              onChange={(value) => updateCustomerFilter(Number(value || 0) || undefined)}
              placeholder="All customers"
            />
            <TimeSelect
              inputId="weekly-project-filter"
              ariaLabel="Project filter"
              options={[
                { value: 0, label: 'All projects' },
                ...filteredProjects.map(project => ({ value: project.id, label: `${project.customerTitle} - ${project.title}` })),
              ]}
              value={filters.projectId || 0}
              onChange={(value) => updateFilter({ projectId: Number(value || 0) || undefined })}
              placeholder="All projects"
            />
            <TimeSelect
              inputId="weekly-billing-filter"
              ariaLabel="Billing filter"
              options={billingFilterOptions}
              value={filters.isBillable === undefined ? 'all' : String(filters.isBillable)}
              onChange={(value) => updateFilter({
                isBillable: value === 'all' ? undefined : value === 'true',
              })}
              placeholder="All billing"
            />
            <input
              className="form-control"
              placeholder="Search"
              value={filters.search || ''}
              onChange={(event) => updateFilter({ search: event.target.value })}
            />
          </div>

          {loading ? (
            <div className="muted-row">Loading...</div>
          ) : weeks.length === 0 ? (
            <div className="empty-state">No weekly data</div>
          ) : (
            weeks.map(week => (
              <div className="week-block" key={week.weekStart}>
                <div className="week-title">
                  {formatDate(week.weekStart)} - {formatDate(week.weekEnd)}
                </div>
                <div className="table-responsive">
                  <table className="table table-hover weekly-table">
                    <thead>
                      <tr>
                        {canViewAllReports && <th>Member</th>}
                        {week.profiles[0]?.days.map(day => (
                          <th key={day.date}>{new Date(day.date).toLocaleDateString(undefined, { weekday: 'short' })}</th>
                        ))}
                        <th>Total</th>
                        <th>Missing</th>
                        <th>Projects</th>
                      </tr>
                    </thead>
                    <tbody>
                      {week.profiles.map(profile => (
                        <tr key={profile.timeProfileId} className={profile.warning ? 'warning-row' : ''}>
                          {canViewAllReports && <td className="weekly-employee-cell" data-label="Member">{profile.timeProfileName}</td>}
                          {profile.days.map(day => {
                            const hasHours = day.totalHours > 0;
                            return (
                              <td
                                className={`weekly-day-cell ${hasHours ? '' : 'weekly-day-empty'}`}
                                key={day.date}
                                data-label={new Date(day.date).toLocaleDateString(undefined, { weekday: 'short' })}
                              >
                                {hasHours ? formatDurationMinutes(day.totalMinutes) : ''}
                              </td>
                            );
                          })}
                          <td className="weekly-total-cell" data-label="Total">{formatDurationMinutes(profile.totalMinutes)}</td>
                          <td className="weekly-missing-cell" data-label="Missing">{profile.missingHours ? formatDurationMinutes(Math.round(profile.missingHours * 60)) : ''}</td>
                          <td className="weekly-projects-cell" data-label="Projects">
                            {profile.projects.length > 0 ? (
                              <div className="project-breakdown">
                                {profile.projects.map(project => (
                                  <div className="project-breakdown-row" key={project.id || project.key}>
                                    <span>{project.key}</span>
                                    <strong>{formatDurationMinutes(project.totalMinutes)}</strong>
                                  </div>
                                ))}
                              </div>
                            ) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default WeeklyTimesheetPage;
